import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useMemo, useState } from "react";
import { Linking, Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

import {
  type ChildrenStatus,
  type EstateReadinessInput,
  EstateReadinessInputSchema,
  FINANCIAL_DISCLAIMER,
  type IntestacyInput,
  IntestacyInputSchema,
  type IntestacyState,
  INTESTACY_STATES,
  type KinStatus,
  LEGAL_CAVEAT,
  type PartnerStatus,
  distributionBullets,
  distributionHeadline,
  readinessBullets,
  readinessHeadline,
} from "@aucash/rules/wills-intestacy";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, OptionList, Segmented, YesNo } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// The two stateless compute responses — derived from the tRPC client so the
// screen's result shapes can never drift from the API. Both resolve through the
// orchestrator-stitched `calc.willsIntestacy.{distribution,readiness}` queries;
// the API runs the pure @aucash/rules engine and echoes its output + sources.
type DistributionResultData = Awaited<
  ReturnType<typeof trpc.calc.willsIntestacy.distribution.query>
>;
type ReadinessResultData = Awaited<
  ReturnType<typeof trpc.calc.willsIntestacy.readiness.query>
>;

// wills-intestacy — STATELESS-v1 surface (docs/surfacing/PLAN.md decision #1).
// Two surfaces, one screen, switched by a mode toggle:
//   * "distribution" — the statutory split if you die without a will (NSW/VIC/
//     QLD), date-keyed (the statutory legacy reprices).
//   * "readiness"    — the 4-document estate-readiness signal.
//
// Compute model: this stateless tool calls the orchestrator-stitched tRPC
// queries calc.willsIntestacy.{distribution,readiness} in an async submit
// handler and holds the response in useState (the canonical client pattern,
// matching au06/au08/au13 + the sibling take-home/packaging-shock screens). The
// API runs the PURE @aucash/rules engine, so every figure still comes from
// @aucash/rules and is never hard-coded here; the assessment date is passed in
// (never the clock) so the right statutory legacy is chosen. The screen only
// formats the echoed output via the rules copy helpers.
//
// REGULATORY (binding — this is a SUCCESSION tool): info-not-advice AND
// not-legal-advice. We render BOTH the financial disclaimer and the legal
// caveat (see a solicitor / your state trustee) on the result, verbatim from
// the rules copy. The wills affiliate (Safewill) stays OFF this surface — facts
// only; no handoff from a personalised result.

type Mode = "distribution" | "readiness";

const STATE_LABEL: Record<IntestacyState, string> = {
  NSW: "New South Wales",
  VIC: "Victoria",
  QLD: "Queensland",
};

const PARTNER_OPTIONS: ReadonlyArray<{ value: PartnerStatus; label: string; sub: string }> = [
  {
    value: "partner",
    label: "I have a partner",
    sub: "A married/de facto spouse, including separated-but-not-divorced",
  },
  { value: "none", label: "No partner", sub: "Single, widowed or fully divorced" },
];

const CHILDREN_OPTIONS: ReadonlyArray<{ value: ChildrenStatus; label: string; sub: string }> = [
  { value: "none", label: "No surviving children", sub: "No children or other issue" },
  {
    value: "all-shared",
    label: "Children, all with my partner",
    sub: "Every surviving child is also my partner's child",
  },
  {
    value: "some-other",
    label: "Children from another relationship",
    sub: "At least one child is from a prior/other relationship (step-family)",
  },
];

const KIN_OPTIONS: ReadonlyArray<{ value: KinStatus; label: string }> = [
  { value: "parents", label: "A surviving parent" },
  { value: "siblings", label: "Surviving siblings" },
  { value: "none", label: "No surviving parents or siblings" },
];

// Today's date, supplied as the assessment date so the statutory legacy in force
// now is the one used. Read at render (a UI default, not inside the pure engine).
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function WillsIntestacyIndex(): JSX.Element {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("distribution");

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Wills & estate" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>Dying without a will · estate readiness</Eyebrow>
            <Text style={styles.title}>What happens to your estate without a will?</Text>
            <Text style={styles.subtitle}>
              See how your state's intestacy rules would split your estate if you
              died without a will, or check whether your four key documents are in
              place. General information drawn from each state's succession Act —
              not legal advice.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What do you want to check?">
            <Segmented<Mode>
              options={[
                { value: "distribution", label: "Who inherits" },
                { value: "readiness", label: "Am I ready?" },
              ]}
              value={mode}
              onChange={setMode}
            />
          </Field>

          {mode === "distribution" ? <Distribution /> : <Readiness />}
        </View>
      </ScrollView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Distribution surface — the statutory split.
// ---------------------------------------------------------------------------

function Distribution(): JSX.Element {
  const [state, setState] = useState<IntestacyState>("NSW");
  const [partner, setPartner] = useState<PartnerStatus | null>(null);
  const [children, setChildren] = useState<ChildrenStatus | null>(null);
  const [kin, setKin] = useState<KinStatus>("none");
  const [estateValue, setEstateValue] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<DistributionResultData | null>(null);

  const assessmentDate = useMemo(() => todayIso(), []);

  const hasPartner = partner === "partner";
  const hasIssue = children !== null && children !== "none";
  const showKin = partner === "none" && children === "none";

  const input = useMemo<IntestacyInput | null>(() => {
    if (partner === null || children === null) return null;
    const raw: IntestacyInput = {
      state,
      partner,
      children,
      kin: showKin ? kin : "none",
      assessmentDate,
      ...(estateValue.trim() !== "" ? { estateValue: Number(estateValue) } : {}),
    };
    const parsed = IntestacyInputSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }, [state, partner, children, kin, showKin, estateValue, assessmentDate]);

  const canSubmit = input !== null && !submitting;

  // Reset the rendered result on any input edit so figures never trail the form.
  const reset = (): void => {
    setResult(null);
    setSubmitError(null);
  };

  // The statutory split is computed by the API (the pure @aucash/rules engine
  // runs server-side) — figures + sources come back in the response, never
  // hard-coded here (arch rule §1). Stateless query, result held in useState.
  const onSubmit = (): void => {
    if (input === null || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.willsIntestacy.distribution
      .query(input)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Field label="Which state were you living in?">
        <Segmented<IntestacyState>
          options={INTESTACY_STATES.map((value) => ({ value, label: value }))}
          value={state}
          onChange={(s) => {
            setState(s);
            reset();
          }}
        />
        <Text style={styles.help}>{STATE_LABEL[state]}. WA, SA, TAS, ACT and NT are coming soon.</Text>
      </Field>

      <Field label="Do you have a partner?">
        <OptionList<PartnerStatus>
          options={PARTNER_OPTIONS}
          value={partner}
          onChange={(v) => {
            setPartner(v);
            reset();
          }}
          testIDPrefix="partner"
        />
      </Field>

      <Field label="Do you have surviving children?">
        <OptionList<ChildrenStatus>
          options={CHILDREN_OPTIONS}
          value={children}
          onChange={(v) => {
            setChildren(v);
            reset();
          }}
          testIDPrefix="children"
        />
        {hasPartner && hasIssue ? (
          <Text style={styles.help}>
            Whether children are all your partner's or from another relationship is the single
            biggest factor in who inherits.
          </Text>
        ) : null}
      </Field>

      {showKin ? (
        <Field
          label="Any surviving parents or siblings?"
          help="With no partner and no children, the estate passes to your next of kin in order — then to the State if there are none."
        >
          <OptionList<KinStatus>
            options={KIN_OPTIONS}
            value={kin}
            onChange={(v) => {
              setKin(v);
              reset();
            }}
            testIDPrefix="kin"
          />
        </Field>
      ) : null}

      <Field
        label="Approximate net estate value (optional)"
        help="Add a figure to see the split in dollars — including the case where the estate is below the statutory legacy, so your partner takes everything. Leave blank to see the split as shares only."
      >
        <MoneyInput
          value={estateValue}
          onChange={(v) => {
            setEstateValue(v);
            reset();
          }}
          testID="input-estate-value"
          accessibilityLabel="Approximate net estate value"
        />
      </Field>

      <Button
        label={submitting ? "Calculating…" : "See the statutory split →"}
        onPress={onSubmit}
        disabled={!canSubmit}
        full
        testID="wills-distribution-submit"
      />
      {submitError !== null ? (
        <Text style={styles.submitError}>{submitError}</Text>
      ) : null}

      {result !== null ? <DistributionResult result={result} /> : null}
    </>
  );
}

function DistributionResult({
  result,
}: {
  result: DistributionResultData;
}): JSX.Element {
  const { output, sources, sourcesVerifiedAt } = result;
  // distributionBullets renders the per-share recitation, the source line, the
  // estate-only note, the QLD multiple-spouse note (when relevant) AND the two
  // mandatory caveats (LEGAL_CAVEAT + FINANCIAL_DISCLAIMER) — all from the rules
  // copy, so the wording can never drift.
  const bullets = distributionBullets(output, sourcesVerifiedAt);

  return (
    <View style={styles.resultBlock}>
      <View style={styles.resHead}>
        <Eyebrow>Statutory distribution · {output.state}</Eyebrow>
        <Chip label="● Intestacy default" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{distributionHeadline(output)}</Text>
      </Card>

      <Section title="What the rules say">
        {bullets.map((b, i) => (
          <Bullet key={i} text={b} />
        ))}
      </Section>

      <LegalBlock />

      <Sources
        sources={sources}
        verifiedAt={sourcesVerifiedAt}
        note={`${output.act}, ${output.distributionRef}`}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Readiness surface — the 4-document signal.
// ---------------------------------------------------------------------------

function Readiness(): JSX.Element {
  const [hasWill, setHasWill] = useState<boolean | null>(null);
  const [lifeEventSinceWill, setLifeEventSinceWill] = useState<boolean | null>(null);
  const [hasEpoa, setHasEpoa] = useState<boolean | null>(null);
  const [hasGuardianship, setHasGuardianship] = useState<boolean | null>(null);
  const [hasMinorChildren, setHasMinorChildren] = useState<boolean | null>(null);
  const [hasGuardianNomination, setHasGuardianNomination] = useState<boolean | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ReadinessResultData | null>(null);

  const input = useMemo<EstateReadinessInput | null>(() => {
    if (
      hasWill === null ||
      hasEpoa === null ||
      hasGuardianship === null ||
      hasMinorChildren === null
    )
      return null;
    const raw: EstateReadinessInput = {
      hasWill,
      lifeEventSinceWill: hasWill ? lifeEventSinceWill ?? false : false,
      hasEnduringPowerOfAttorney: hasEpoa,
      hasEnduringGuardianship: hasGuardianship,
      hasMinorChildren,
      hasGuardianNomination: hasMinorChildren ? hasGuardianNomination ?? false : false,
    };
    const parsed = EstateReadinessInputSchema.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }, [
    hasWill,
    lifeEventSinceWill,
    hasEpoa,
    hasGuardianship,
    hasMinorChildren,
    hasGuardianNomination,
  ]);

  // Reset the rendered result on any input edit so it never trails the form.
  const reset = (): void => {
    setResult(null);
    setSubmitError(null);
  };

  // Compute server-side (pure @aucash/rules engine runs in the API) — the
  // readiness picture comes back in the response; nothing is derived in-screen.
  const onSubmit = (): void => {
    if (input === null || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.willsIntestacy.readiness
      .query(input)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Field label="Do you have a will?">
        <YesNo value={hasWill} onChange={(v) => { setHasWill(v); reset(); }} testIDPrefix="has-will" />
      </Field>

      {hasWill === true ? (
        <Field
          label="Any marriage, divorce or new child since you made it?"
          help="Marriage or divorce can revoke or partly invalidate a will in several states, so a recent life event may have affected it."
        >
          <YesNo
            value={lifeEventSinceWill}
            onChange={(v) => { setLifeEventSinceWill(v); reset(); }}
            testIDPrefix="life-event"
          />
        </Field>
      ) : null}

      <Field label="Do you have an enduring power of attorney (finances)?">
        <YesNo value={hasEpoa} onChange={(v) => { setHasEpoa(v); reset(); }} testIDPrefix="has-epoa" />
      </Field>

      <Field label="Do you have an enduring guardian / medical decision-maker?">
        <YesNo
          value={hasGuardianship}
          onChange={(v) => { setHasGuardianship(v); reset(); }}
          testIDPrefix="has-guardianship"
        />
      </Field>

      <Field label="Do you have dependent (minor) children?">
        <YesNo
          value={hasMinorChildren}
          onChange={(v) => { setHasMinorChildren(v); reset(); }}
          testIDPrefix="has-minor-children"
        />
      </Field>

      {hasMinorChildren === true ? (
        <Field label="Have you nominated a guardian for them in a will?">
          <YesNo
            value={hasGuardianNomination}
            onChange={(v) => { setHasGuardianNomination(v); reset(); }}
            testIDPrefix="has-guardian-nomination"
          />
        </Field>
      ) : null}

      <Button
        label={submitting ? "Checking…" : "Check my readiness →"}
        onPress={onSubmit}
        disabled={input === null || submitting}
        full
        testID="wills-readiness-submit"
      />
      {submitError !== null ? (
        <Text style={styles.submitError}>{submitError}</Text>
      ) : null}

      {result !== null ? <ReadinessResult result={result} /> : null}
    </>
  );
}

function ReadinessResult({ result }: { result: ReadinessResultData }): JSX.Element {
  const { output } = result;
  // readinessBullets renders one factual line per document plus the two
  // mandatory caveats — all from the rules copy.
  const bullets = readinessBullets(output);

  return (
    <View style={styles.resultBlock}>
      <View style={styles.resHead}>
        <Eyebrow>Estate readiness</Eyebrow>
        <Chip label={output.actionSuggested ? "● Needs attention" : "● All in place"} tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{readinessHeadline(output)}</Text>
      </Card>

      <Section title="Your four key documents">
        {bullets.map((b, i) => (
          <Bullet key={i} text={b} />
        ))}
      </Section>

      <LegalBlock />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Shared result pieces.
// ---------------------------------------------------------------------------

/** The mandatory not-legal-advice + not-financial-advice block, shown verbatim
 * on every result. The two caveat strings come from the rules copy. */
function LegalBlock(): JSX.Element {
  return (
    <View style={styles.legalBlock}>
      <Text style={styles.legalText}>{LEGAL_CAVEAT}</Text>
      <Text style={styles.legalText}>{FINANCIAL_DISCLAIMER}</Text>
    </View>
  );
}

function Sources({
  sources,
  verifiedAt,
  note,
}: {
  sources: Record<string, string>;
  verifiedAt: string;
  note: string;
}): JSX.Element {
  const entries = Object.entries(sources);
  return (
    <Section title="Where these rules come from">
      {entries.map(([key, url]) => (
        <SourceLink key={key} label={SOURCE_LABEL[key] ?? key} url={url} />
      ))}
      <Text style={styles.verified}>
        {note} · last verified {verifiedAt}
      </Text>
    </Section>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  act: "The governing succession Act",
  s106: "Statutory legacy provision (s 106)",
  s113: "Blended-family distribution (s 113)",
  s70m: "Statutory legacy provision (s 70M)",
  schedule2: "Distribution schedule (Sch 2)",
  statutoryLegacy: "Current statutory legacy figure",
  plainEnglish: "Plain-English state guidance",
};

function SourceLink({ label, url }: { label: string; url: string }): JSX.Element {
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

function Bullet({ text }: { text: string }): JSX.Element {
  return (
    <View style={styles.whyRow}>
      <View style={styles.whyIcon}>
        <Text style={styles.whyIconText}>i</Text>
      </View>
      <Text style={styles.whyText}>{text}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  help: {
    fontFamily: fonts.body,
    fontSize: 13,
    lineHeight: 19,
    color: colors.inkSoft,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },

  resultBlock: { gap: space.xl, marginTop: space.sm },
  resHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  lead: { fontFamily: fonts.bodyMedium, fontSize: 16, lineHeight: 23, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 20, letterSpacing: -0.4, color: colors.ink },

  whyRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  whyIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  whyIconText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.honeyDeep },
  whyText: { flex: 1, fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  legalBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    gap: space.sm,
  },
  legalText: { fontFamily: fonts.body, fontSize: 12.5, lineHeight: 19, color: colors.inkSoft },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
